import * as React from 'react';
import { connect } from 'react-redux';

import '../Main.scss';
import '../ImportCustomerFinancials/NewConnectionModal.scss';
import './DestinationStep.scss';

import { Button } from '../design-system/components';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase, faSpinnerThird } from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

import { FieldGuideEngagementItem, PortalTextCommon } from '../../Models/Api/strongbox.financialportal';
import {
    FieldGuideListEngagementsResult,
    ListFieldGuideEngagements,
    isFieldGuideListEngagementsFailure,
} from '../../Services/FieldGuideService';
import { ApplicationState } from '../../Store';
import {
    FieldGuideCompanySelection,
    FieldGuideEngagementSelection,
    GetFieldGuideSelectedCompany,
    GetFieldGuideSelectedEngagement,
    actionCreators as FieldGuideStoreActions,
} from '../../Store/FieldGuide';
import { GetPortalLanguageCode, GetPortalText } from '../../Store/PortalAssets';

import { DestinationCard } from './DestinationCard';
import { DisabledSearchBox } from './DisabledSearchBox';

type InjectedReduxState = {
    selectedCompany: FieldGuideCompanySelection | undefined;
    selectedEngagement: FieldGuideEngagementSelection | undefined;
    commonText: PortalTextCommon | undefined;
    // The portal's language (e.g. 'en', 'de', 'es'); the created date is formatted in it. Optional: falls back to en-US.
    languageCode?: string;
};

type InjectedActionCreators = {
    SelectEngagement: (engagement: FieldGuideEngagementSelection) => void;
    NextUploadStep: () => void;
    PreviousUploadStep: () => void;
};

type EngagementStepProps = {
    onCancel: () => void;
    // Overridable so tests need not stub the network; defaults to the real service.
    listEngagements?: (companyId: string, options?: { signal?: AbortSignal }) => Promise<FieldGuideListEngagementsResult>;
};

type Props = EngagementStepProps & InjectedReduxState & InjectedActionCreators;

type EngagementList =
    | { status: 'loading' }
    | { status: 'loaded'; engagements: FieldGuideEngagementItem[] }
    // message is the server's own message, when it sent one; otherwise the generic portal text is shown.
    | { status: 'failed'; message?: string };

const FallbackDateLocale = 'en-US';

/** The portal language as a locale the runtime supports, or en-US when it is missing, malformed or unsupported. */
export function dateLocaleFor(languageCode: string | undefined): string {
    if (!languageCode) {
        return FallbackDateLocale;
    }

    try {
        const [supported] = Intl.DateTimeFormat.supportedLocalesOf([languageCode]);
        return supported || FallbackDateLocale;
    } catch {
        // supportedLocalesOf throws a RangeError for a tag that is not valid BCP-47.
        return FallbackDateLocale;
    }
}

/** "August 1, 2026" (en) from the instant, in the viewer's zone and the portal language; undefined when absent or unparseable. */
function formatCreated(createdAt: string | undefined, languageCode: string | undefined): string | undefined {
    if (!createdAt) {
        return undefined;
    }

    const date = new Date(createdAt);
    return isNaN(date.getTime()) ? undefined : date.toLocaleDateString(dateLocaleFor(languageCode), { dateStyle: 'long' });
}

export const EngagementStepComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        onCancel, listEngagements = ListFieldGuideEngagements, selectedCompany, selectedEngagement, commonText, languageCode,
        SelectEngagement, NextUploadStep, PreviousUploadStep,
    } = props;

    // One-shot, like the cards: a second click on Next (or on a card after Next) must not dispatch a second advance.
    const advanced = React.useRef(false);
    const advance = (): void => {
        if (advanced.current) {
            return;
        }
        advanced.current = true;
        NextUploadStep();
    };

    const [list, setList] = React.useState<EngagementList>({ status: 'loading' });
    const companyId = selectedCompany?.id;

    React.useEffect(() => {
        if (companyId === undefined) {
            return undefined;
        }

        // Aborted when the client changes or the step unmounts, so an abandoned request is cancelled, not just ignored.
        const controller = new AbortController();
        setList({ status: 'loading' });

        const load = async (): Promise<void> => {
            try {
                const result = await listEngagements(companyId, { signal: controller.signal });
                if (controller.signal.aborted) {
                    return;
                }

                setList(isFieldGuideListEngagementsFailure(result)
                    ? { status: 'failed', message: result.message }
                    : { status: 'loaded', engagements: result.response.items });
            } catch {
                if (!controller.signal.aborted) {
                    setList({ status: 'failed' });
                }
            }
        };
        void load();

        return () => controller.abort();
        // Keyed on the client only: listEngagements is a stable service default (or a test double).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    const renderIntro = (): React.ReactNode => {
        const template = commonText?.fieldGuideSelectEngagementIntro || 'Choose the engagement under {0}. Only open and pending engagements are shown.';
        const [before, ...rest] = template.split('{0}');
        return (
            <p className={'page-body-text'}>
                {before}<strong>{selectedCompany?.name}</strong>{rest.join('{0}')}
            </p>
        );
    };

    const renderList = (): React.ReactNode => {
        if (list.status === 'loading') {
            return (
                <div className={'page-centered-content'} role={'status'} aria-label={(commonText?.fieldGuideLoadingEngagements || 'Loading engagements')}>
                    <FontAwesomeIcon icon={faSpinnerThird} spin className={'page-large-icon'} />
                </div>
            );
        }

        if (list.status === 'failed') {
            return <p className={'page-body-text'} role={'alert'}>{list.message || commonText?.fieldGuideLoadEngagementsFailed || 'We could not load your Fieldguide engagements. Please try again, and contact support if the problem continues.'}</p>;
        }

        if (list.engagements.length === 0) {
            return <p className={'page-body-text'}>{commonText?.fieldGuideNoEngagements || 'No open or pending engagements found for this client.'}</p>;
        }

        return (
            <div className={'page-stack page-stack--fill'}>
                <DisabledSearchBox
                    placeholder={commonText?.fieldGuideSearchEngagements || 'Search engagements'}
                    hoverText={commonText?.fieldGuideSearchComingSoon || 'Search is coming in story #41109.'}
                />
                <div className={'page-stack page-stack--small page-scroll-list'}>
                    {list.engagements.map(engagement => (
                        <DestinationCard
                            key={engagement.id}
                            icon={faBriefcase}
                            heading={engagement.name}
                            description={formatCreated(engagement.createdAt, languageCode)}
                            selected={selectedEngagement?.id === engagement.id}
                            onSelect={() => {
                                SelectEngagement({ id: engagement.id, name: engagement.name });
                                advance();
                            }}
                        />
                    ))}
                </div>
            </div>
        );
    };

    const canAdvance = list.status === 'loaded' && selectedEngagement !== undefined
        && list.engagements.some(engagement => engagement.id === selectedEngagement.id);

    return (
        <div className={'field-guide-step'}>
            <div className={'field-guide-step__content page-stack'}>
                {renderIntro()}
                {renderList()}
            </div>
            <div className={'field-guide-footer'}>
                <Button type={'floating'} onClick={onCancel}>{commonText?.cancel || 'Cancel'}</Button>
                <div className={'cr-modal-footer__button-group'}>
                    <Button type={'secondary'} className={'page-btn--min-width'} onClick={PreviousUploadStep}>{commonText?.backButton || 'Back'}</Button>
                    <Button type={'primary'} className={'page-btn--min-width'} disabled={!canAdvance} onClick={advance}>{commonText?.nextButton || 'Next'}</Button>
                </div>
            </div>
        </div>
    );
};

export const EngagementStepContainer = connect<InjectedReduxState, InjectedActionCreators, EngagementStepProps, ApplicationState>(
    (appState: ApplicationState) => {
        const result: InjectedReduxState = {
            selectedCompany: GetFieldGuideSelectedCompany(appState),
            selectedEngagement: GetFieldGuideSelectedEngagement(appState),
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            languageCode: GetPortalLanguageCode(appState),
        };
        return result;
    },
    {
        SelectEngagement: FieldGuideStoreActions.SelectEngagement,
        NextUploadStep: FieldGuideStoreActions.NextUploadStep,
        PreviousUploadStep: FieldGuideStoreActions.PreviousUploadStep,
    }
)(EngagementStepComponent);
